import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useUser, useClerk, useAuth } from '@clerk/clerk-react'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, useStripe } from '@stripe/react-stripe-js'
import GooglePayButton from '@google-pay/button-react'

/**
 * Express Checkout Page with Google Pay Support
 * 
 * Features:
 * - Modern Stripe Express Checkout Element for Google Pay, Apple Pay, Link
 * - Monthly/Annual plan selection toggle
 * - Clerk authentication integration
 * - SortingCat plan loading using Stripe plan IDs
 * - Responsive design matching SortingCat theme
 */

// Initialize Stripe with publishable key
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
const stripePromise = stripeKey && stripeKey.startsWith('pk_') 
  ? loadStripe(stripeKey) 
  : null // Prevent loading with invalid key

interface PlanOption {
  id: 'apprentice' | 'professional'
  name: string
  monthlyPrice: number
  annualPrice: number
  monthlyPriceId: string
  annualPriceId: string
  features: string[]
  recommended?: boolean
}

const ExpressCheckoutForm: React.FC = () => {
  const { isSignedIn, user, isLoaded } = useUser()
  const { getToken } = useAuth()
  const { openSignUp } = useClerk()
  const stripe = useStripe()
  
  const [selectedPlan, setSelectedPlan] = useState<'apprentice' | 'professional'>('apprentice')
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual')
  const [isProcessing, setIsProcessing] = useState(false)
  const [expressCheckoutOptions, setExpressCheckoutOptions] = useState<any>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const handleSuccess = useCallback(async () => {
    if (!user) return

    setIsProcessing(true)
    try {
      const response = await fetch('/.netlify/functions/success', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${await getToken() || ''}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setSuccessMessage('🎉 Payment successful! Your subscription has been activated.')
        
        // Redirect to account page after a delay
        setTimeout(() => {
          window.location.href = data.redirectUrl || '/account'
        }, 2000)
      } else {
        throw new Error('Failed to sync subscription data')
      }
    } catch (error) {
      console.error('Error processing success:', error)
      setSuccessMessage('Payment completed, but there was an issue syncing your account. Please contact support if needed.')
    } finally {
      setIsProcessing(false)
    }
  }, [user, getToken])

  // Check for success/cancel query parameters
  useEffect(() => {
    // eslint-disable-next-line no-undef
    const urlParams = new URLSearchParams(window.location.search)
    const success = urlParams.get('success')
    const cancelled = urlParams.get('cancelled')

    if (success === 'true') {
      handleSuccess()
    } else if (cancelled === 'true') {
      setSuccessMessage('Payment was cancelled. You can try again anytime.')
    }
  }, [handleSuccess])

  // Plan configuration with actual Stripe plan IDs
  const plans: PlanOption[] = useMemo(() => [
    {
      id: 'apprentice',
      name: 'Apprentice',
      monthlyPrice: 4.99,
      annualPrice: 4.00,
      monthlyPriceId: process.env.REACT_APP_CLERK_APPRENTICE_PLAN_ID || 'cplan_334CC8yTqPIPXxNs7wG5tBtwaBF',
      annualPriceId: process.env.REACT_APP_CLERK_APPRENTICE_PLAN_ID || 'cplan_334CC8yTqPIPXxNs7wG5tBtwaBF', // Assuming same ID for now
      recommended: true,
      features: [
        '2,000 images categorized monthly',
        'Advanced AI descriptions',
        'Custom categories',
        'Email support'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      monthlyPrice: 11.79,
      annualPrice: 8.69,
      monthlyPriceId: 'cplan_335YlprdpORXLgnWjGP0vrRQ0GJ',
      annualPriceId: 'cplan_335YlprdpORXLgnWjGP0vrRQ0GJ', // Assuming same ID for now
      features: [
        '5,000 images categorized monthly',
        'Advanced categories and filtering',
        'Priority processing',
        'Advanced AI descriptions',
        'Custom categories',
        'Priority email support'
      ]
    }
  ], [])

  const currentPlan = plans.find(p => p.id === selectedPlan)!
  const currentPrice = billingCycle === 'monthly' ? currentPlan.monthlyPrice : currentPlan.annualPrice
  const currentPriceId = billingCycle === 'monthly' ? currentPlan.monthlyPriceId : currentPlan.annualPriceId

  // T3 Pattern: Create checkout session when plan selection changes
  useEffect(() => {
    if (!stripe || !isSignedIn || !currentPlan) return

    const createCheckoutSession = async () => {
      try {
        const response = await fetch('/.netlify/functions/generate-stripe-checkout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${await getToken() || ''}`,
          },
          body: JSON.stringify({
            priceId: currentPriceId,
            planName: currentPlan.name,
            billingCycle,
            successUrl: `${window.location.origin}/express-checkout?success=true`,
            cancelUrl: `${window.location.origin}/express-checkout?cancelled=true`,
          }),
        })

        if (!response.ok) {
          const error = await response.json()
          throw new Error(error.message || 'Failed to create checkout session')
        }

        const { sessionId, url } = await response.json()
        
        // Store session info for Google Pay button
        setExpressCheckoutOptions({
          sessionId,
          checkoutUrl: url,
          appearance: {
            theme: 'stripe' as const,
            variables: {
              colorPrimary: '#9333ea', // Purple-600 to match SortingCat theme
            },
          },
        })
      } catch (error) {
        console.error('Error creating checkout session:', error)
        // Show user-friendly error
        setExpressCheckoutOptions(null)
      }
    }

    createCheckoutSession()
  }, [stripe, isSignedIn, currentPriceId, currentPlan, billingCycle, currentPrice, user, getToken])

  // Note: Express checkout is now handled via direct Stripe checkout sessions
  // The T3 pattern uses redirect-based checkout instead of client-side processing

  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <header className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img src="/sorting_cat_mascot.png" alt="SortingCat Mascot" className="h-36 w-36 object-cover rounded-full border-2 border-purple-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-purple-500 to-purple-700 bg-clip-text text-transparent mb-2">
            Express Checkout
          </h1>
          <p className="text-lg text-purple-600 font-medium">
            Pay with Google Pay, Apple Pay, or Link - Fast & Secure
          </p>
          <p className="text-sm text-gray-600 mt-2">
            ✅ Works on Desktop & Mobile • Google Pay available in Chrome & Edge browsers
          </p>
        </header>

        {/* Success Message */}
        {successMessage && (
          <div className={`rounded-lg p-6 mb-8 ${successMessage.includes('🎉') 
            ? 'bg-green-50 border border-green-200' 
            : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                {successMessage.includes('🎉') ? (
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                ) : (
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 18.5c-.77.833.192 2.5 1.732 2.5z"></path>
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`font-medium ${successMessage.includes('🎉') ? 'text-green-800' : 'text-yellow-800'}`}>
                  {successMessage}
                </p>
                {successMessage.includes('🎉') && (
                  <p className="text-green-700 text-sm mt-1">
                    Redirecting to your account...
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Authentication Check */}
        {!isSignedIn && !successMessage && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 18.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-yellow-800">Sign Up Required</h3>
                <p className="text-yellow-700 mt-1">Please sign up to continue with express checkout.</p>
                <button
                  onClick={() => openSignUp()}
                  className="mt-3 bg-purple-600 text-white px-4 py-2 rounded-md font-medium hover:bg-purple-700 transition-colors"
                >
                  Sign Up
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Plan Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Choose Your Plan</h2>
          
          {/* Billing Cycle Toggle */}
          <div className="flex justify-center mb-6">
            <div className="bg-gray-100 rounded-lg p-1 flex">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-4 py-2 rounded-md font-medium transition-colors ${
                  billingCycle === 'monthly'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`px-4 py-2 rounded-md font-medium transition-colors relative ${
                  billingCycle === 'annual'
                    ? 'bg-white text-purple-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Annual
                <span className="absolute -top-2 -right-1 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                  Save
                </span>
              </button>
            </div>
          </div>

          {/* Plan Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`cursor-pointer rounded-lg border-2 p-6 transition-all ${
                  selectedPlan === plan.id
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                } ${plan.recommended ? 'relative' : ''}`}
              >
                {plan.recommended && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                      Recommended
                    </span>
                  </div>
                )}
                
                <div className="text-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <div className="mb-2">
                    {billingCycle === 'annual' ? (
                      <div>
                        <div className="text-lg text-gray-500 line-through">${plan.monthlyPrice}</div>
                        <div className="text-3xl font-bold text-gray-900">${plan.annualPrice}</div>
                      </div>
                    ) : (
                      <div className="text-3xl font-bold text-gray-900">${plan.monthlyPrice}</div>
                    )}
                    <p className="text-gray-600">per month</p>
                    {billingCycle === 'annual' && (
                      <p className="text-sm text-green-600 font-medium">Billed annually</p>
                    )}
                  </div>
                </div>
                
                <ul className="space-y-2">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-center text-sm text-gray-700">
                      <svg className="w-4 h-4 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Express Checkout Section */}
        {isSignedIn && expressCheckoutOptions && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Express Checkout - {currentPlan.name}
            </h2>
            
            <div className="text-center mb-6">
              <div className="text-lg text-gray-600 mb-2">
                {billingCycle === 'annual' ? 'Annual' : 'Monthly'} Subscription
              </div>
              <div className="text-3xl font-bold text-purple-600">
                ${currentPrice}/month
              </div>
              {billingCycle === 'annual' && (
                <div className="text-sm text-green-600 font-medium">
                  Save ${((currentPlan.monthlyPrice - currentPlan.annualPrice) * 12).toFixed(2)}/year
                </div>
              )}
            </div>

            {/* T3 Pattern: Direct Checkout with Google Pay Support */}
            <div className="max-w-md mx-auto space-y-4">
              {expressCheckoutOptions?.checkoutUrl ? (
                <>
                  {/* Primary Google Pay / Stripe Checkout Button */}
                  <button
                    onClick={() => {
                      window.location.href = expressCheckoutOptions.checkoutUrl;
                    }}
                    className="w-full bg-black text-white py-4 px-6 rounded-lg font-medium text-lg hover:bg-gray-800 transition-colors flex items-center justify-center space-x-3"
                  >
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12.426 19.95c4.24 0 7.736-3.497 7.736-7.736s-3.496-7.736-7.736-7.736-7.736 3.497-7.736 7.736 3.496 7.736 7.736 7.736z" fill="#4285F4"/>
                      <path d="M10.76 9.833h3.92v1.667h-3.92v-1.667zm0 2.5h3.92v1.667h-3.92V12.333zm0 2.5h2.453v1.667H10.76v-1.667z" fill="white"/>
                    </svg>
                    <span>Pay with Google Pay & Cards</span>
                  </button>
                  
                  {/* Alternative: Official Google Pay Button (if preferred) */}
                  <GooglePayButton
                    environment="TEST" // Change to "PRODUCTION" for live
                    paymentRequest={{
                      apiVersion: 2,
                      apiVersionMinor: 0,
                      allowedPaymentMethods: [
                        {
                          type: 'CARD',
                          parameters: {
                            allowedAuthMethods: ['PAN_ONLY', 'CRYPTOGRAM_3DS'],
                            allowedCardNetworks: ['MASTERCARD', 'VISA', 'AMEX']
                          },
                          tokenizationSpecification: {
                            type: 'PAYMENT_GATEWAY',
                            parameters: {
                              gateway: 'stripe',
                              // Stripe handles merchant ID automatically
                            }
                          }
                        }
                      ],
                      merchantInfo: {
                        merchantId: '01234567890123456789', // Test merchant ID - Stripe will override
                        merchantName: 'SortingCat AI'
                      },
                      transactionInfo: {
                        totalPriceStatus: 'FINAL',
                        totalPriceLabel: `${currentPlan.name} Plan - ${billingCycle}`,
                        totalPrice: currentPrice.toString(),
                        currencyCode: 'USD'
                      }
                    }}
                    onLoadPaymentData={(paymentData) => {
                      console.log('Google Pay payment data:', paymentData);
                      // Redirect to Stripe checkout with Google Pay data
                      window.location.href = expressCheckoutOptions.checkoutUrl;
                    }}
                    onError={(error) => {
                      console.error('Google Pay error:', error);
                    }}
                    onCancel={() => {
                      console.log('Google Pay cancelled');
                    }}
                    existingPaymentMethodRequired={false}
                    buttonColor="black"
                    buttonType="subscribe"
                    buttonSizeMode="fill"
                  />
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Preparing checkout session...</p>
                </div>
              )}
            </div>

            {isProcessing && (
              <div className="text-center mt-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto mb-2"></div>
                <p className="text-gray-600">Processing payment...</p>
              </div>
            )}
          </div>
        )}

        {/* Benefits Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Why Express Checkout?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-purple-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Lightning Fast</h3>
              <p className="text-gray-600 text-sm">Complete your purchase in seconds with saved payment methods</p>
            </div>
            <div className="text-center">
              <div className="bg-green-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Secure</h3>
              <p className="text-gray-600 text-sm">Bank-level security with tokenized payments and fraud protection</p>
            </div>
            <div className="text-center">
              <div className="bg-blue-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Desktop + Mobile</h3>
              <p className="text-gray-600 text-sm">Works everywhere: Chrome/Edge browsers, mobile Touch/Face ID, Google Pay</p>
            </div>
          </div>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <a 
            href="/plans"
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md font-medium hover:bg-gray-200 transition-colors inline-flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
            </svg>
            Back to Plans
          </a>
        </div>
      </div>
    </div>
  )
}

const ExpressCheckoutPage: React.FC = () => {
  // Check if Stripe is properly configured
  if (!stripePromise) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 max-w-md mx-auto">
          <div className="text-center">
            <div className="bg-yellow-100 rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.314 18.5c-.77.833.192 2.5 1.732 2.5z"></path>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment System Configuration Required</h2>
            <p className="text-gray-600 mb-4">
              Express Checkout is not available. Please contact support or try the regular plans page.
            </p>
            <a 
              href="/plans"
              className="bg-purple-600 text-white px-4 py-2 rounded-md font-medium hover:bg-purple-700 transition-colors inline-flex items-center"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path>
              </svg>
              Back to Plans
            </a>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Elements stripe={stripePromise}>
      <ExpressCheckoutForm />
    </Elements>
  )
}

export const Route = createFileRoute('/express-checkout')({
  component: ExpressCheckoutPage,
})